import React, { useState } from 'react';
import styles from './ExecutionLogCard.module.css';

/**
 * Mirrors the ProgressEvent shape emitted by agent-progress blocks.
 * Only fields relevant to the execution log are listed here.
 */
interface ProgressEvent {
  type?: string;
  status?: string;
  state?: string;
  tool?: string;
  stage?: string;
  detail?: string;
  taskId?: string;
  role?: string;
  feature?: string;
  completedTaskId?: string;
  targets?: { cloud?: string[]; public?: string[]; data?: string[] };
  changed?: { cloud?: string[]; public?: string[]; data?: string[] };
}

interface CategorizedFiles {
  cloud: string[];
  public: string[];
  data: string[];
}

interface PhaseInfo {
  taskId: string;
  role: string;
  feature: string;
  completed: boolean;
  targetFiles: CategorizedFiles;
  changedFiles: CategorizedFiles;
}

function safePaths(arr?: string[]): string[] {
  return Array.isArray(arr) ? arr.filter(Boolean) : [];
}

function emptyCategorized(): CategorizedFiles {
  return { cloud: [], public: [], data: [] };
}

function countCategorized(c: CategorizedFiles): number {
  return c.public.length + c.cloud.length + c.data.length;
}

/**
 * Groups raw progress events by taskId into a structured per-phase summary,
 * preserving cloud/public/data categories instead of flattening.
 */
function buildPhases(events: ProgressEvent[]): PhaseInfo[] {
  const map = new Map<string, PhaseInfo>();
  const order: string[] = [];

  for (const evt of events) {
    const tid = evt.taskId;
    if (!tid) continue;

    if (!map.has(tid)) {
      map.set(tid, {
        taskId: tid,
        role: '',
        feature: '',
        completed: false,
        targetFiles: emptyCategorized(),
        changedFiles: emptyCategorized(),
      });
      order.push(tid);
    }

    const phase = map.get(tid)!;

    if (evt.role && !phase.role) phase.role = evt.role;
    if (evt.feature && !phase.feature) phase.feature = evt.feature;

    if (evt.tool === 'phase_start' && evt.targets) {
      const t = evt.targets;
      if (countCategorized(phase.targetFiles) === 0) {
        phase.targetFiles = {
          cloud: safePaths(t.cloud),
          public: safePaths(t.public),
          data: safePaths(t.data),
        };
      }
    }

    if (evt.tool === 'phase_done') {
      phase.completed = true;
      if (evt.changed) {
        const c = evt.changed;
        const cat: CategorizedFiles = {
          cloud: safePaths(c.cloud),
          public: safePaths(c.public),
          data: safePaths(c.data),
        };
        if (countCategorized(cat) > 0) {
          phase.changedFiles = cat;
        }
      }
    }

    if (evt.tool === 'complete_task' && evt.status === 'done') {
      phase.completed = true;
    }
  }

  return order.map((tid) => map.get(tid)!);
}

/**
 * Returns the best available file set for a phase (prefer changed over targets).
 */
function bestFiles(phase: PhaseInfo): CategorizedFiles {
  return countCategorized(phase.changedFiles) > 0
    ? phase.changedFiles
    : phase.targetFiles;
}

/**
 * Deduplicate an array of strings preserving order.
 */
function dedup(arr: string[]): string[] {
  return Array.from(new Set(arr));
}

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const LogIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 8h8M8 12h6M8 16h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/**
 * ExecutionLogCard - Persistent collapsible execution summary shown in chat
 * after an EXECUTION round finishes.
 *
 * Collapsed: shows task names with file counts.
 * Expanded: groups all changes by Frontend (public) and Backend (cloud + data).
 */
export default function ExecutionLogCard({ events }: { events: ProgressEvent[] }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const phases = React.useMemo(() => buildPhases(events), [events]);

  // Total unique file count across all phases for the header badge.
  const totalFileCount = React.useMemo(() => {
    const allPublic: string[] = [];
    const allCloud: string[] = [];
    const allData: string[] = [];
    for (const p of phases) {
      const f = bestFiles(p);
      allPublic.push(...f.public);
      allCloud.push(...f.cloud);
      allData.push(...f.data);
    }
    const total = new Set([...allPublic, ...allCloud, ...allData]);
    return total.size;
  }, [phases]);

  // Aggregated Frontend / Backend file lists for expanded view.
  const { frontendFiles, backendFiles, dataEntries } = React.useMemo(() => {
    const pubAll: string[] = [];
    const cloudAll: string[] = [];
    const dataAll: string[] = [];
    for (const p of phases) {
      const f = bestFiles(p);
      pubAll.push(...f.public);
      cloudAll.push(...f.cloud);
      dataAll.push(...f.data);
    }
    return {
      frontendFiles: dedup(pubAll),
      backendFiles: dedup(cloudAll),
      dataEntries: dedup(dataAll),
    };
  }, [phases]);

  if (!phases.length) return null;

  return (
    <div className={styles.card}>
      {/* Header */}
      <div
        className={styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <LogIcon className={styles.headerIcon} />
        <span className={styles.headerTitle}>Execution log</span>
        <span className={styles.headerCount}>
          {totalFileCount} {totalFileCount === 1 ? 'file' : 'files'} modified
        </span>
        <svg
          className={styles.chevron}
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Collapsed: task list with file counts */}
      {!isExpanded && phases.length > 0 && (
        <div className={styles.collapsedBody}>
          {phases.map((phase) => {
            const files = bestFiles(phase);
            const count = countCategorized(files);
            return (
              <div key={phase.taskId} className={styles.collapsedRow}>
                {phase.completed && <CheckIcon className={styles.phaseCheck} />}
                <span className={styles.collapsedName}>
                  {phase.feature || phase.taskId}
                </span>
                {count > 0 && (
                  <span className={styles.collapsedCount}>
                    {count} {count === 1 ? 'file' : 'files'}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Expanded: grouped by Frontend / Backend */}
      {isExpanded && (
        <div className={styles.body}>
          {/* Frontend section */}
          {frontendFiles.length > 0 && (
            <div className={styles.categorySection}>
              <div className={styles.categoryHeader}>
                <span className={styles.categoryBadgeFrontend}>Frontend</span>
                <span className={styles.categoryCount}>
                  {frontendFiles.length} {frontendFiles.length === 1 ? 'file' : 'files'}
                </span>
              </div>
              <div className={styles.filesList}>
                {frontendFiles.map((f) => (
                  <span key={f} className={styles.fileChip}>{f}</span>
                ))}
              </div>
            </div>
          )}

          {/* Backend section */}
          {(backendFiles.length > 0 || dataEntries.length > 0) && (
            <div className={styles.categorySection}>
              <div className={styles.categoryHeader}>
                <span className={styles.categoryBadgeBackend}>Backend</span>
                <span className={styles.categoryCount}>
                  {backendFiles.length + dataEntries.length}{' '}
                  {backendFiles.length + dataEntries.length === 1 ? 'item' : 'items'}
                </span>
              </div>
              <div className={styles.filesList}>
                {backendFiles.map((f) => (
                  <span key={f} className={styles.fileChip}>{f}</span>
                ))}
                {dataEntries.map((d) => (
                  <span key={d} className={styles.fileChipData}>{d}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
